// Search in the offline copy, for when the server cannot be reached. The syntax is the server's (every word must
// occur, OR between alternatives, "quoted phrase", -word to exclude) but the matching is simpler: a word matches
// where a word of the message begins with it ("faktura" finds "fakturaen"; with Exact match only a whole word), case
// and accents ignored, without the server's stemming or its semantic search, and newest first. Plain functions,
// testable without the DOM.

import type { Message, MessageRow, SearchParams } from "./api";
import { fold } from "./format";
import type { CachedRow } from "./store";

export interface Term {
  text: string;
  exclude: boolean;
}

/** Every group must match: one of its terms occurs (a term with exclude: does not occur). */
export type Query = Term[][];

const EDGE = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu;
const WORD_CHAR = /[\p{L}\p{N}]/u;
/** The body text the search looks in, at most (the server indexes a capped body as well). */
const TEXT_CHARS = 100_000;

export function parseQuery(q: string): Query {
  const groups: Query = [];
  let joinNext = false;
  for (const m of q.matchAll(/(-?)"([^"]*)"?|(\S+)/g)) {
    if (m[3]?.toUpperCase() === "OR") {
      joinNext = groups.length > 0;
      continue;
    }
    const exclude = m[2] !== undefined ? m[1] === "-" : m[3].startsWith("-") && m[3].length > 1;
    const raw = m[2] !== undefined ? m[2] : exclude ? m[3].slice(1) : m[3];
    const text = fold(raw).replace(/\s+/g, " ").replace(EDGE, "");
    if (!text) continue;
    const term = { text, exclude };
    if (joinNext && !exclude) groups[groups.length - 1].push(term);
    else groups.push([term]);
    joinNext = false;
  }
  return groups;
}

/** `word` occurs in `text` where a word begins (whole: and ends where that word ends). */
export function hasWordStart(text: string, word: string, whole = false): boolean {
  for (let i = text.indexOf(word); i !== -1; i = text.indexOf(word, i + 1)) {
    const end = i + word.length;
    if ((i === 0 || !WORD_CHAR.test(text[i - 1])) && (!whole || end === text.length || !WORD_CHAR.test(text[end]))) return true;
  }
  return false;
}

/** exact: the words as written, whole words only ("faktura" no longer finds "fakturaen"). */
export function matches(query: Query, text: string, exact = false): boolean {
  return query.every((group) => group.some((t) => hasWordStart(text, t.text, exact) !== t.exclude));
}

/** What the search looks in: subject, sender, recipients, attachment names and body, folded. */
export function searchText(row: Pick<CachedRow, "subject" | "from_name" | "from_addr" | "to" | "cc" | "attachments">, body: string | null): string {
  const parts = [row.subject, row.from_name, row.from_addr, ...(row.to ?? []), ...(row.cc ?? []), ...row.attachments.map((a) => a.filename), (body ?? "").slice(0, TEXT_CHARS)];
  return fold(parts.filter(Boolean).join("\n"));
}

/** A whole address (something, @, a domain with a dot): the sent_to filter matches it exactly, anything else as a
 * part, as the server does (search._ADDRESS_RE). */
const WHOLE_ADDRESS = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;

/** Whether the message has an attachment of its own, as the server counts them: not only images its body shows (logos).
 * From the attachments, not has_attachments: a message kept from before the server counted that way has it as it was. */
export function hasAttachments(row: Pick<CachedRow, "attachments">): boolean {
  return row.attachments.some((a) => !a.inline);
}

/** The filters of a search that the rows alone decide (all but the words and those on the folders, which the copy's
 * locations tell). */
export function passesFilters(row: CachedRow, p: SearchParams): boolean {
  if (!p.include_deleted && row.deleted_at) return false;
  if (p.has_attachment && !hasAttachments(row)) return false;
  if (p.from && !fold(`${row.from_name ?? ""} ${row.from_addr ?? ""}`).includes(fold(p.from.trim()))) return false;
  if (p.to && !(row.to ?? []).concat(row.cc ?? []).some((a) => fold(a).includes(fold(p.to!.trim())))) return false;
  const day = row.date?.slice(0, 10) ?? null; // dates are UTC, as the server's days
  if (p.after && (day === null || day < p.after)) return false;
  if (p.before && (day === null || day > p.before)) return false;
  const sentTo = p.sent_to?.trim().toLowerCase();
  if (sentTo) {
    const address = row.delivery?.sent_to ?? null;
    if (!address || !(WHOLE_ADDRESS.test(sentTo) ? address === sentTo : address.includes(sentTo))) return false;
  }
  return true;
}

/** Trash and the spam folders, as the server names them (ingest.TRASH_FOLDERS, ingest.SPAM_FOLDERS): a star there
 * does not put the message among the starred mail, as in Yahoo's own Starred tab. */
const TRASH_FOLDERS = ["trash", "deleted"];
const UNSTARRED_FOLDERS = [...TRASH_FOLDERS, "bulk", "bulk mail", "spam", "spam mail", "junk", "scams"];

/** In Trash and no other folder, as Exclude Trash leaves it out on the server (search._TRASH_ONLY_IDS); mail in no
 * Yahoo folder any more is not. */
export function onlyInTrash(folders: Message["folders"]): boolean {
  return folders.length > 0 && folders.every((f) => TRASH_FOLDERS.includes(f.folder.toLowerCase()));
}

/** Starred as a search for the starred mail counts it, as the server does: in `folder`, or without one in any folder
 * but Trash and spam. */
export function starredIn(folders: Message["folders"], folder: string | null): boolean {
  const counts = (name: string) => (folder ? name.toLowerCase() === folder.toLowerCase() : !UNSTARRED_FOLDERS.includes(name.toLowerCase()));
  return folders.some((f) => f.flags.includes("\\Flagged") && counts(f.folder));
}

/** A listing row from the offline copy; unread and flagged from the message's flags in `flags` (its folder's, or
 * all its folders' together; null when it is in no Yahoo folder any more, which counts as read). */
export function listingRow(row: CachedRow, flags: string[] | null): MessageRow {
  const names = row.attachments.filter((a) => !a.inline && a.filename).map((a) => a.filename);
  return {
    id: row.id,
    date: row.date,
    from_addr: row.from_addr,
    from_name: row.from_name,
    subject: row.subject,
    snippet: row.snippet,
    folders: row.folders.map((f) => f.folder),
    has_attachments: hasAttachments(row),
    attachment_names: names.length ? names.join(", ") : null,
    matched_attachment: null,
    deleted_at: row.deleted_at,
    sent_to: row.delivery?.sent_to ?? null,
    flags: flags ?? [],
    unread: flags !== null && !flags.includes("\\Seen"),
    flagged: flags?.includes("\\Flagged") ?? false,
  };
}
